import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Brain, Heart, ThumbsDown, Repeat, CalendarClock, Sparkles, Trash2, Plus, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const MEMORY_TYPES = [
  { key: "favourite", label: "Favourite", icon: Heart, bg: "bg-[#FCE1E8]", ic: "text-sorted-rose", hint: "e.g. Ivy loves spag bol" },
  { key: "dislike", label: "Dislike", icon: ThumbsDown, bg: "bg-[#E7E0F3]", ic: "text-purple-500", hint: "e.g. Sam won't touch mushrooms" },
  { key: "weekly_ritual", label: "Weekly ritual", icon: Repeat, bg: "bg-[#FCEFCF]", ic: "text-amber-500", hint: "e.g. Tacos", day: true },
  { key: "recurring_event", label: "Recurring event", icon: CalendarClock, bg: "bg-[#DEE7F5]", ic: "text-blue-500", hint: "e.g. Soccer training", day: true },
  { key: "habit", label: "Habit", icon: Sparkles, bg: "bg-[#DDEBE1]", ic: "text-emerald-600", hint: "e.g. We eat early on school nights" },
];

export default function MillieMemory() {
  const { activeId, activeHousehold, loadHouseholds } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/memories/${activeId}`); setData(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const forget = async (m) => {
    try { await api.delete(`/memories/${m.id}`); toast.success(`Forgotten: ${m.text}`); await load(); loadHouseholds?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const memories = data?.memories || [];
  const grouped = MEMORY_TYPES.map((t) => ({ ...t, items: memories.filter((m) => m.type === t.key) })).filter((g) => g.items.length);

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] pb-28">
        <div className="px-6 pt-12 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="memory-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">What Millie remembers</h1>
        </div>

        <div className="relative h-44 mt-1">
          <MillieHero mood="thinking" className="absolute right-0 bottom-0 h-44 w-[44%]" testId="memory-millie-hero" />
          <div className="absolute left-6 top-2 max-w-[54%]">
            <SpeechBubble tail="br" testId="memory-millie-bubble">
              {memories.length ? `I've got ${memories.length} thing${memories.length === 1 ? "" : "s"} tucked away about ${activeHousehold?.name || "the fam"}. Tell me more anytime.` : "Nothing yet! Tell me your rituals, favourites and busy nights and I'll plan around them."}
            </SpeechBubble>
          </div>
        </div>

        <div className="px-6 space-y-5">
          {data?.busy_nights_memory?.length > 0 && (
            <p className="text-xs font-bold text-sorted-slate bg-white/70 rounded-2xl px-3 py-2" data-testid="memory-busy-note">
              <CalendarClock size={12} className="inline mr-1" strokeWidth={2.5} />Because of your events, {data.busy_nights_memory.join(" & ")} {data.busy_nights_memory.length === 1 ? "is" : "are"} quick-dinner nights.
            </p>
          )}

          {!adding ? (
            <button onClick={() => setAdding(true)} data-testid="memory-add-btn" className="w-full bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft"><Plus size={18} strokeWidth={2.5} /> Tell Millie something</button>
          ) : (
            <AddMemory activeId={activeId} members={activeHousehold?.members || []} onDone={async () => { setAdding(false); await load(); loadHouseholds?.(); }} onCancel={() => setAdding(false)} />
          )}

          {grouped.map((g) => (
            <div key={g.key} data-testid={`memory-group-${g.key}`}>
              <h3 className="font-hand text-2xl text-sorted-ink flex items-center gap-2"><g.icon size={18} strokeWidth={2.5} className={g.ic} />{g.label}s</h3>
              <div className="mt-2 space-y-2">
                {g.items.map((m) => (
                  <div key={m.id} className={`${g.bg} rounded-3xl border-2 border-white/60 p-3 flex items-center gap-3 shadow-soft`} data-testid={`memory-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sorted-ink leading-tight">{m.member ? <span className="font-bold">{m.member}: </span> : null}{m.text}</p>
                      <p className="text-[11px] font-semibold text-sorted-slate mt-0.5">
                        {m.day ? `Every ${m.day} · ` : ""}{m.source === "noticed" ? <span className="inline-flex items-center gap-1"><Eye size={11} strokeWidth={2.5} /> Millie noticed</span> : "You told Millie"}{m.times_seen > 1 ? ` · seen ${m.times_seen}×` : ""}
                      </p>
                    </div>
                    <button onClick={() => forget(m)} data-testid={`memory-forget-${m.id}`} aria-label="Forget" className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center text-sorted-slate"><Trash2 size={15} strokeWidth={2.5} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {data && !memories.length && (
            <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-5 text-center shadow-soft" data-testid="memory-empty">
              <Brain size={28} strokeWidth={2} className="mx-auto text-sorted-rose" />
              <p className="text-sm text-sorted-slate mt-2">Try telling me in chat: "We always have tacos on Tuesdays" or "Ivy loves spag bol". I'll remember it and plan around it.</p>
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

function AddMemory({ activeId, members, onDone, onCancel }) {
  const [type, setType] = useState("weekly_ritual");
  const [text, setText] = useState("");
  const [day, setDay] = useState("Tuesday");
  const [member, setMember] = useState("");
  const [saving, setSaving] = useState(false);
  const cfg = MEMORY_TYPES.find((t) => t.key === type);

  const save = async () => {
    if (!text.trim()) return toast.error("Tell Millie what to remember");
    setSaving(true);
    try {
      await api.post("/memories", { household_id: activeId, type, text: text.trim(), day: cfg.day ? day : null, member });
      toast.success("Got it — I'll remember that");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft space-y-3" data-testid="memory-form">
      <div className="flex flex-wrap gap-2">
        {MEMORY_TYPES.map((t) => (
          <button key={t.key} onClick={() => setType(t.key)} data-testid={`memory-type-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${type === t.key ? "bg-sorted-rose border-sorted-rose text-white" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>{t.label}</button>
        ))}
      </div>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={cfg.hint} data-testid="memory-text"
        className="w-full rounded-2xl border-2 border-[#FDECEF] px-4 py-3 text-sm font-semibold text-sorted-ink outline-none focus:border-sorted-rose" />
      {cfg.day && (
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {DAYS.map((d) => (
            <button key={d} onClick={() => setDay(d)} data-testid={`memory-day-${d}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border-2 ${day === d ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>{d.slice(0, 3)}</button>
          ))}
        </div>
      )}
      {members.length > 0 && (
        <select value={member} onChange={(e) => setMember(e.target.value)} data-testid="memory-member"
          className="w-full rounded-2xl border-2 border-[#FDECEF] px-4 py-3 text-sm font-semibold text-sorted-ink bg-white">
          <option value="">Whole household</option>
          {members.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
      )}
      <div className="flex gap-2">
        <button onClick={onCancel} data-testid="memory-cancel" className="flex-1 py-3 rounded-full border-2 border-[#FDECEF] text-sm font-bold text-sorted-slate">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="memory-save" className="flex-1 py-3 rounded-full bg-sorted-rose text-white text-sm font-bold">{saving ? "Saving…" : "Remember it"}</button>
      </div>
    </div>
  );
}
